import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Send, Settings2, CheckCircle, AlertCircle, Loader2, RefreshCw, Smartphone, Monitor } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { usePushNotifications } from '../contexts/PushNotificationContext';
import { setSoundEnabled, updateSoundPrefs } from '../utils/notificationSound';
import SoundsAlertsCard from './settings/SoundsAlertsCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationPreferences() {
  const {
    isInitialized,
    isSubscribed,
    isLoading: contextLoading,
    permission,
    subscribe,
    unsubscribe,
    sendTestNotification,
    refreshRegistration,
    isConfigured,
  } = usePushNotifications();

  const [preferences, setPreferences] = useState({
    job_assignments: true,
    status_updates: true,
    appointment_reminders: true,
    daily_summaries: true,
    new_messages: true,
    message_preview: true,
    notification_sound: true,
    sms_tone: 'default',
    call_ringtone: 'default',
    call_ring_enabled: true,
    ring_volume: 1,
    location_ringtones_enabled: true,
    custom_sms_url: null,
    custom_ring_url: null,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [deviceStatus, setDeviceStatus] = useState(null);

  // Fetch user preferences on mount
  useEffect(() => {
    fetchPreferences();
    fetchDeviceStatus();
  }, []);

  const getToken = () => localStorage.getItem('token') || localStorage.getItem('tech_token');

  // Mirror sound-related prefs into the local cache used at playback time
  const mirrorSoundPrefs = (p) => {
    setSoundEnabled(p.notification_sound !== false);
    updateSoundPrefs({
      sms_enabled: p.notification_sound !== false,
      sms_tone: p.sms_tone || 'default',
      call_ringtone: p.call_ringtone || 'default',
      call_ring_enabled: p.call_ring_enabled !== false,
      ring_volume: typeof p.ring_volume === 'number' ? p.ring_volume : 1,
      location_ringtones_enabled: p.location_ringtones_enabled !== false,
      custom_sms_url: p.custom_sms_url || null,
      custom_ring_url: p.custom_ring_url || null,
    });
  };

  const fetchDeviceStatus = async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API}/push/subscription`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
      });
      if (res.ok) setDeviceStatus(await res.json());
    } catch { /* non-critical */ }
  };

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await fetch(`${API}/push/preferences`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setPreferences(data);
        mirrorSoundPrefs(data);
      }
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async (newPreferences) => {
    try {
      setSaving(true);
      const token = getToken();
      const response = await fetch(`${API}/push/preferences`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify(newPreferences),
      });
      
      if (response.ok) {
        toast.success('Preferences saved');
      } else {
        toast.error('Failed to save preferences');
      }
    } catch (error) {
      console.error('Failed to save preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handlePreferenceChange = (key) => {
    const newPreferences = {
      ...preferences,
      [key]: !preferences[key],
    };
    setPreferences(newPreferences);
    if (key === 'notification_sound') {
      setSoundEnabled(newPreferences.notification_sound);
    }
    savePreferences(newPreferences);
  };

  // Set one or more preference values at once (used by Sounds & Alerts)
  const handlePrefValues = (partial) => {
    const newPreferences = { ...preferences, ...partial };
    setPreferences(newPreferences);
    mirrorSoundPrefs(newPreferences);
    savePreferences(newPreferences);
  };

  const handleRefreshRegistration = async () => {
    setRefreshing(true);
    try {
      await refreshRegistration();
      await fetchDeviceStatus();
    } finally {
      setRefreshing(false);
    }
  };

  const handleSubscriptionToggle = async () => {
    if (isSubscribed) {
      await unsubscribe();
    } else {
      await subscribe();
    }
  };

  const getPermissionStatus = () => {
    if (!isConfigured) {
      return { text: 'Not Configured', color: 'bg-gray-500', icon: AlertCircle };
    }
    if (permission === 'granted' && isSubscribed) {
      return { text: 'Subscribed', color: 'bg-green-500', icon: CheckCircle };
    }
    if (permission === 'denied') {
      return { text: 'Blocked', color: 'bg-red-500', icon: AlertCircle };
    }
    return { text: 'Not Subscribed', color: 'bg-yellow-500', icon: BellOff };
  };

  const status = getPermissionStatus();
  const StatusIcon = status.icon;

  if (!isConfigured) {
    return (
      <Card data-testid="notification-preferences-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-base sm:text-lg">Push Notifications</CardTitle>
          </div>
          <CardDescription>
            Push notifications are not configured
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-muted-foreground">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">Push notifications are currently unavailable</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="notification-preferences-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-base sm:text-lg">Push Notifications</CardTitle>
          </div>
          <Badge className={`${status.color} text-white`}>
            <StatusIcon className="h-3 w-3 mr-1" />
            {status.text}
          </Badge>
        </div>
        <CardDescription>
          Receive instant notifications on your device for important events
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Subscription Toggle */}
        <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
          <div className="space-y-1">
            <p className="font-medium text-sm sm:text-base">Enable Push Notifications</p>
            <p className="text-xs sm:text-sm text-muted-foreground">
              {isSubscribed 
                ? 'You will receive notifications on this device' 
                : 'Subscribe to receive push notifications'}
            </p>
          </div>
          <Button
            onClick={handleSubscriptionToggle}
            disabled={contextLoading || permission === 'denied'}
            variant={isSubscribed ? 'default' : 'outline'}
            size="sm"
            data-testid="subscribe-toggle-btn"
          >
            {contextLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isSubscribed ? (
              <>
                <BellOff className="h-4 w-4 mr-2" />
                Disable
              </>
            ) : (
              <>
                <Bell className="h-4 w-4 mr-2" />
                Enable
              </>
            )}
          </Button>
        </div>

        {permission === 'denied' && (
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
            <p className="text-sm text-red-400">
              Push notifications are blocked in your browser. Please enable them in your browser settings.
            </p>
          </div>
        )}

        {/* Device / registration status */}
        <div className="p-4 border rounded-lg space-y-2" data-testid="notification-device-status">
          <div className="flex items-center justify-between">
            <span className="font-medium text-sm">Device Status</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleRefreshRegistration}
              disabled={refreshing}
              className="h-7 text-xs"
              data-testid="refresh-registration-btn"
            >
              {refreshing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
              Refresh Registration
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Bell className="h-3.5 w-3.5" />
              Permission:{' '}
              <span className={permission === 'granted' ? 'text-green-500 font-medium' : permission === 'denied' ? 'text-red-500 font-medium' : 'text-yellow-500 font-medium'}>
                {permission === 'granted' ? 'Granted' : permission === 'denied' ? 'Blocked' : 'Not asked'}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Monitor className="h-3.5 w-3.5" />
              Browser:{' '}
              <span className={deviceStatus?.web_subscribed ? 'text-green-500 font-medium' : 'text-muted-foreground'}>
                {deviceStatus?.web_subscribed ? 'Registered' : 'Not registered'}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Smartphone className="h-3.5 w-3.5" />
              Mobile:{' '}
              <span className={deviceStatus?.mobile_subscribed ? 'text-green-500 font-medium' : 'text-muted-foreground'}>
                {deviceStatus?.mobile_subscribed ? `${deviceStatus.mobile_devices} device${deviceStatus.mobile_devices > 1 ? 's' : ''}` : 'Not registered'}
              </span>
            </div>
          </div>
          {deviceStatus?.updated_at && (
            <p className="text-[10px] text-muted-foreground">
              Last registration: {new Date(deviceStatus.updated_at).toLocaleString()}
            </p>
          )}
        </div>

        {/* Sounds & Alerts — per-user SMS tone and in-app call ringtone */}
        <SoundsAlertsCard preferences={preferences} onPrefChange={handlePrefValues} saving={saving} />

        <div className="grid gap-3">
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <Label htmlFor="message_preview" className="text-sm font-medium cursor-pointer">
                Show Message Preview
              </Label>
              <p className="text-xs text-muted-foreground">
                When off, notifications say "New SMS received" instead of showing the message content
              </p>
            </div>
            <Switch
              id="message_preview"
              checked={preferences.message_preview}
              onCheckedChange={() => handlePreferenceChange('message_preview')}
              disabled={saving}
              data-testid="pref-message-preview"
            />
          </div>
        </div>

        {/* Notification Preferences */}
        {isSubscribed && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Settings2 className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium text-sm">Notification Types</span>
            </div>
            
            <div className="grid gap-4">
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <Label htmlFor="job_assignments" className="text-sm font-medium cursor-pointer">
                    Job Assignments
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    When a new job is assigned to you or your team
                  </p>
                </div>
                <Switch
                  id="job_assignments"
                  checked={preferences.job_assignments}
                  onCheckedChange={() => handlePreferenceChange('job_assignments')}
                  disabled={saving}
                  data-testid="pref-job-assignments"
                />
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <Label htmlFor="status_updates" className="text-sm font-medium cursor-pointer">
                    Status Updates
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    When job status changes (started, completed, etc.)
                  </p>
                </div>
                <Switch
                  id="status_updates"
                  checked={preferences.status_updates}
                  onCheckedChange={() => handlePreferenceChange('status_updates')}
                  disabled={saving}
                  data-testid="pref-status-updates"
                />
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <Label htmlFor="appointment_reminders" className="text-sm font-medium cursor-pointer">
                    Appointment Reminders
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Reminders before scheduled appointments
                  </p>
                </div>
                <Switch
                  id="appointment_reminders"
                  checked={preferences.appointment_reminders}
                  onCheckedChange={() => handlePreferenceChange('appointment_reminders')}
                  disabled={saving}
                  data-testid="pref-appointment-reminders"
                />
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <Label htmlFor="new_messages" className="text-sm font-medium cursor-pointer">
                    New Messages
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    When you receive new SMS messages
                  </p>
                </div>
                <Switch
                  id="new_messages"
                  checked={preferences.new_messages}
                  onCheckedChange={() => handlePreferenceChange('new_messages')}
                  disabled={saving}
                  data-testid="pref-new-messages"
                />
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <Label htmlFor="daily_summaries" className="text-sm font-medium cursor-pointer">
                    Daily Summaries
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Daily summary of jobs and appointments
                  </p>
                </div>
                <Switch
                  id="daily_summaries"
                  checked={preferences.daily_summaries}
                  onCheckedChange={() => handlePreferenceChange('daily_summaries')}
                  disabled={saving}
                  data-testid="pref-daily-summaries"
                />
              </div>
            </div>

            {/* Test Notification Button */}
            <div className="pt-4 border-t">
              <Button
                onClick={sendTestNotification}
                variant="outline"
                className="w-full"
                data-testid="send-test-notification-btn"
              >
                <Send className="h-4 w-4 mr-2" />
                Send Test Notification
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
